import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { auth, googleProvider, githubProvider } from '../firebase/config';
import AuthService from '../services/authService';

/** Preserve Firebase `code` on thrown errors so callers (e.g. AuthModal) can branch. */
function rejectWithAuthError(message: string, code?: string): never {
  const err = new Error(message) as Error & { code?: string };
  if (code) err.code = code;
  throw err;
}

// Authentication enabled
// To disable for development: Change DISABLE_AUTH to true
const DISABLE_AUTH = false;



// Mock user for development when auth is disabled
const MOCK_USER = {
  uid: 'dev-user-123',
  email: 'developer@mentis-ai.net',
  displayName: 'Development User'
} as User;

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  signInWithGoogle: () => Promise<void>;
  signInWithGithub: () => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(DISABLE_AUTH ? MOCK_USER : null);
  const [loading, setLoading] = useState(true);

  // Show development mode indicator
  useEffect(() => {
    if (DISABLE_AUTH) {
      
      
    } else {
      
    }
  }, []);

  useEffect(() => {
    // TEMPORARY: Skip Firebase auth when disabled
    if (DISABLE_AUTH) {
      setCurrentUser(MOCK_USER);
      setLoading(false);
      return;
    }

    // Handle redirect result on page load
    const handleRedirectResult = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          console.log('✅ OAuth redirect sign-in successful');
          // User signed in via redirect, ensure user exists in all tables
          try {
            const setupResult = await AuthService.ensureUserExists(result.user);
            if (setupResult.created) {
              // New user - wait longer for database operations to complete
              console.log('🆕 New user created via redirect, waiting for billing initialization...');
              await new Promise(resolve => setTimeout(resolve, 2000));
            } else if (setupResult.error) {
              console.error('Failed to setup redirect user:', setupResult.error);
            }
          } catch (authError) {
            console.error('Failed to setup redirect user:', authError);
            // Don't throw - user is still authenticated even if DB setup fails
          }
        }
      } catch (error: any) {
        console.error('❌ Error handling Google redirect result:', error);
        
        // Provide specific error messages
        if (error.code === 'auth/unauthorized-domain') {
          console.error('⚠️ Domain not authorized. Check Firebase Console > Authentication > Settings > Authorized domains');
        } else if (error.code === 'auth/invalid-oauth-client-id') {
          console.error('⚠️ Invalid OAuth client. Check Google Cloud Console > APIs & Services > Credentials');
        } else if (error.code === 'auth/operation-not-allowed') {
          console.error('⚠️ Google sign-in not enabled in Firebase Console');
        } else if (error.message?.includes('redirect_uri_mismatch')) {
          console.error('⚠️ OAuth redirect URI mismatch. Add https://mentis-ai.net/__/auth/handler to Google Cloud Console');
        }
        
        // Don't throw - allow app to continue loading
      }
    };

    handleRedirectResult();

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // For existing users (non-redirect), just set the user
        // The redirect case is already handled above
        setCurrentUser(user);
        setLoading(false);
      } else {
        setCurrentUser(null);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    // TEMPORARY: Skip actual auth when disabled
    if (DISABLE_AUTH) {
      
      setCurrentUser(MOCK_USER);
      return;
    }

    try {
      
      // On desktop, try popup first, fallback to redirect if blocked
      try {
        const result = await signInWithPopup(auth, googleProvider);

        // Ensure user exists in all tables (users, user_profiles, user_billing)
        try {
          const setupResult = await AuthService.ensureUserExists(result.user);
          if (setupResult.created) {
            // Wait for database operations to complete
            console.log('🆕 New user created via popup, waiting for billing initialization...');
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else if (setupResult.error) {
            console.error('Failed to setup Google user:', setupResult.error);
          }
        } catch (authError) {
          console.error('Failed to setup Google user:', authError);
        }

      } catch (popupError: any) {
        if (popupError.code === 'auth/popup-blocked') {
          console.log('🔄 Popup blocked, falling back to redirect method');
          await signInWithRedirect(auth, googleProvider);
          return;
        }
        throw popupError;
      }
    } catch (error: any) {
      console.error('❌ Google sign-in error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to sign in with Google. Please try again.';
      
      if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized for Google sign-in. Please contact support.';
        console.error('⚠️ Domain authorization issue. Check Firebase Console > Authentication > Settings > Authorized domains');
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google sign-in is not enabled. Please contact support.';
        console.error('⚠️ Google provider not enabled in Firebase Console');
      } else if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.code === 'auth/internal-error') {
        errorMessage = 'An internal error occurred. Please try again later.';
        console.error('⚠️ Internal Firebase error:', error.message);
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      rejectWithAuthError(errorMessage, error.code);
    }
  };

  const signInWithGithub = async () => {
    // TEMPORARY: Skip actual auth when disabled
    if (DISABLE_AUTH) {
      
      setCurrentUser(MOCK_USER);
      return;
    }

    try {
      // On desktop, try popup first, fallback to redirect if blocked
      try {
        const result = await signInWithPopup(auth, githubProvider);

        // Ensure user exists in all tables (users, user_profiles, user_billing)
        try {
          const setupResult = await AuthService.ensureUserExists(result.user);
          if (setupResult.error) {
            console.error('Failed to setup GitHub user:', setupResult.error);
          }
        } catch (authError) {
          console.error('Failed to setup GitHub user:', authError);
        }

      } catch (popupError: any) {
        if (popupError.code === 'auth/popup-blocked') {
          await signInWithRedirect(auth, githubProvider);
          return;
        }
        throw popupError;
      }
    } catch (error: any) {
      console.error('❌ GitHub sign-in error:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to sign in with GitHub. Please try again.';
      if (error.code === 'auth/network-request-failed') {
        errorMessage = 'Network error. Please check your internet connection and try again.';
      } else if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign-in was cancelled. Please try again.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      rejectWithAuthError(errorMessage, error.code);
    }
  };

  const signUpWithEmail = async (email: string, password: string) => {
    // TEMPORARY: Skip actual auth when disabled
    if (DISABLE_AUTH) {

      setCurrentUser(MOCK_USER);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Setup complete user profile in all tables (users, user_profiles, user_billing)
      // This MUST complete before we allow navigation
      try {
        const setupResult = await AuthService.setupNewUser(userCredential.user, {
          // Extract additional info from email
          bio: `New user registered with email: ${email}`
        });
        
        if (!setupResult.success) {
          console.error('User setup failed:', setupResult.error);
          // Log the error but don't throw - the auth state is already set
          // The user will be redirected but may need to complete setup later
        } else {
          console.log('🆕 New user created via email, waiting for billing initialization...');
        }
      } catch (authError) {
        console.error('Failed to setup user profile:', authError);
        // Don't throw here - user account is created, setup can be completed later
      }
      
      // Wait for database operations (especially billing) to complete
      await new Promise(resolve => setTimeout(resolve, 2000));
      
    } catch (error: any) {
      console.error('Sign up error:', error);

      // Provide specific error messages based on Firebase error codes
      switch (error.code) {
        case 'auth/email-already-in-use':
          throw new Error('This email is already registered. Please sign in instead.');
        case 'auth/invalid-email':
          throw new Error('Please enter a valid email address.');
        case 'auth/weak-password':
          throw new Error('Password should be at least 6 characters long.');
        case 'auth/operation-not-allowed':
          throw new Error('Email/password accounts are not enabled. Please contact support.');
        case 'auth/network-request-failed':
          throw new Error('Network error. Please check your connection and try again.');
        default:
          throw new Error(`Failed to create account: ${error.message}`);
      }
    }
  };

  const signInWithEmail = async (email: string, password: string) => {
    // TEMPORARY: Skip actual auth when disabled
    if (DISABLE_AUTH) {
      
      setCurrentUser(MOCK_USER);
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: any) {
      console.error('Sign in error:', error);
      
      // Provide specific error messages based on Firebase error codes
      switch (error.code) {
        case 'auth/user-not-found':
          throw new Error('No account found with this email address. Please sign up first.');
        case 'auth/wrong-password':
          throw new Error('Incorrect password. Please try again.');
        case 'auth/invalid-email':
          throw new Error('Please enter a valid email address.');
        case 'auth/user-disabled':
          throw new Error('This account has been disabled. Please contact support.');
        case 'auth/too-many-requests':
          throw new Error('Too many failed attempts. Please try again later.');
        case 'auth/network-request-failed':
          throw new Error('Network error. Please check your connection and try again.');
        case 'auth/invalid-credential':
          throw new Error('Invalid email or password. Please check your credentials.');
        default:
          throw new Error(`Sign in failed: ${error.message}`);
      }
    }
  };

  const logout = async () => {
    // TEMPORARY: Skip actual auth when disabled
    if (DISABLE_AUTH) {
      
      setCurrentUser(null);
      return;
    }

    try {
      await signOut(auth);
    } catch (error) {
      
      throw error;
    }
  };

  const value = {
    currentUser,
    loading,
    signInWithGoogle,
    signInWithGithub,
    signUpWithEmail,
    signInWithEmail,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
